import { useDispatch, useSelector } from "react-redux";
import useFetchExchanges from "../../../hooks/api/useFetchExchanges";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getAvailableCoins } from "../../../redux/exchange/actions";
import { getLiveSuggestion, normalizeLiveTimeframe } from "../../../redux/live-suggestion/liveSuggestionSlice";
import styles from "../../../styles/Components/new-insight/chart-section/ExchangePairConfigs.module.css";
import { FaChevronDown, FaStore } from "react-icons/fa6";
import { Select } from "antd";
import { FaSyncAlt } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { useSearchParams } from "react-router-dom";
import useServiceStatus from "../../../hooks/useServiceStatus";

function ExchangePairConfigs() {
  const {
    exchangeState: { availableCoinsData, availableCoinsStatus },
    main: { userData },
    liveSuggestion: { suggestions, coinStatus, liveTimeframe },
    newInsightState: { insightMarketType },
  } = useSelector((state) => state);

  const isCryptoMarket = (insightMarketType || "crypto") === "crypto";

  const { isActive: isInsightActive } = useServiceStatus("insight");

  const [searchParams, setSearchParams] = useSearchParams();

  const controllerRef = useRef(null);

  const selectedExchange = searchParams.get("exchange") || "binance";

  const selectedCoin = searchParams.get("symbol") || "BTC";

  const initialMount = useRef(null);
  const chartTfRef = useRef("");

  const setSelectedCoin = useCallback(
    (coin) => {
      searchParams.set("symbol", coin);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const setSelectedExchange = useCallback(
    (exchange) => {
      searchParams.set("exchange", exchange);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const { exchanges, loading: exchangesLoading } = useFetchExchanges();

  const dispatch = useDispatch();

  const isAssetsLoading =
    availableCoinsStatus?.[selectedExchange] === "loading";
  const assetsList = availableCoinsData?.[selectedExchange] || [];

  const isSignalLoading = coinStatus?.[selectedCoin] === "loading";

  const exchangeOptions = useMemo(() => {
    return exchangesLoading
      ? [{ value: "", label: "Loading..." }]
      : exchanges?.map((ex) => {
          return { value: ex.id, label: ex.name };
        });
  }, [exchangesLoading, exchanges]);

  const coinOptions = useMemo(() => {
    return isAssetsLoading
      ? [{ value: "", label: "Assets Loading..." }]
      : assetsList.map((asset) => {
          return { value: asset, label: asset };
        });
  }, [assetsList, isAssetsLoading]);

  const selectExchange = (ex) => {
    if (!ex) return;
    setSelectedExchange(ex);

    // fetch available coins if the exchanges coin did not fetch yet
    if (!availableCoinsData?.[ex]) {
      dispatch(getAvailableCoins(ex));
    }
  };

  const selectAsset = (asset) => {
    if (!asset) return;
    setSelectedCoin(asset);
    if (!isCryptoMarket) return;

    // Create a new AbortController
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;
    dispatch(
      getLiveSuggestion(
        userData?.["custom:custom_username"],
        userData?.["cognito:username"],
        selectedExchange,
        asset,
        true,
        signal,
        false,
        false,
        0,
        [],
        normalizeLiveTimeframe(liveTimeframe),
      ),
    );
  };

  const cancelSuggestion = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  };

  useEffect(() => {
    if (
      selectedExchange &&
      !availableCoinsData?.[selectedExchange] &&
      userData?.email &&
      !initialMount.current &&
      isInsightActive
    ) {
      dispatch(getAvailableCoins(selectedExchange));
    }
  }, [selectedExchange, dispatch, availableCoinsData, userData?.email]);

  // fetch signal based initial info
  useEffect(() => {
    if (
      userData?.["custom:custom_username"] &&
      isInsightActive &&
      isCryptoMarket &&
      (!initialMount.current || chartTfRef.current !== (normalizeLiveTimeframe(liveTimeframe)))
    ) {
      controllerRef.current = new AbortController();
      const { signal } = controllerRef.current;

      initialMount.current = true;
      chartTfRef.current = normalizeLiveTimeframe(liveTimeframe);

      dispatch(
        getLiveSuggestion(
          userData?.["custom:custom_username"],
          userData?.["cognito:username"],
          selectedExchange,
          selectedCoin,
          false,
          signal,
          false,
          false,
          0,
          [],
          normalizeLiveTimeframe(liveTimeframe),
        ),
      );

      // clean-up function to cancel request
      return () => {
        controllerRef.current?.abort();
      };
    }
  }, [dispatch, isCryptoMarket, liveTimeframe, selectedExchange, selectedCoin, userData]);

  return (
    <div className={styles.configBox}>
      {!isInsightActive ? (
        <h5>Please activate Insights service first!</h5>
      ) : (
        <>
          <div className={styles.configs}>
            <div className={styles.configItem}>
              {/*<label>*/}
              {/*  <FaStore />*/}
              {/*  Exchange:*/}
              {/*</label>*/}
              <Select
                value={selectedExchange}
                disabled={exchangesLoading || isSignalLoading}
                options={exchangeOptions}
                suffixIcon={<FaChevronDown color="#0f1c53" />}
                onChange={selectExchange}
              />
            </div>
            <div className={styles.configItem}>
              {/*<label>*/}
              {/*  <FaSyncAlt />*/}
              {/*  Pair:*/}
              {/*</label>*/}
              <Select
                value={selectedCoin}
                disabled={isSignalLoading || isAssetsLoading}
                options={coinOptions}
                suffixIcon={<FaChevronDown color="#0f1c53" />}
                onChange={selectAsset}
              />
            </div>
            {!isAssetsLoading &&
              selectedExchange &&
              assetsList?.length === 0 && (
                <h5 style={{ marginBottom: 0 }}>
                  You need to integrate with this exchange first!
                </h5>
              )}
          </div>
          {isSignalLoading && controllerRef.current && (
            <span onClick={cancelSuggestion} className={styles.cancelBtn}>
              <IoIosCloseCircle size={24} onClick={cancelSuggestion} />
            </span>
          )}
        </>
      )}
    </div>
  );
}

export default ExchangePairConfigs;
